import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import type { ReactNode } from 'react'

/**
 * The four pieces every Home section is built from.
 *
 * Two rules are enforced here rather than repeated in six section files:
 *
 * 1. **A number is a link.** `StatTile` is a `<Link>`, always, and it takes a
 *    `to` that already carries the destination's filter (`/work?tab=progress`,
 *    not `/work`). The old dashboard's counts were `<div>`s, so the only way to
 *    act on "3 awaiting review" was to guess which tab held them.
 * 2. **Amber is reserved for human attention.** `tone="attention"` colours the
 *    number only when it is non-zero, so a page of grey tiles with one amber
 *    number reads as "that one is on you". Pipeline-owned counts stay grey.
 */

export function Section({
  title,
  description,
  action,
  children,
}: {
  title: string
  description: string
  /** Right-aligned link into the surface that owns this section. */
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-h3 font-semibold tracking-tight">{title}</h2>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        {action}
      </div>
      {children}
    </section>
  )
}

export function TileGrid({ children }: { children: ReactNode }) {
  return <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
}

export function StatTile({
  to,
  label,
  value,
  hint,
  emptyHint,
  tone = 'neutral',
  loading = false,
}: {
  /** Pre-filtered destination. A tile that lands on an unfiltered list is a bug. */
  to: string
  label: string
  value: number
  hint: string
  /** Shown instead of `hint` at zero — "nothing here" needs different copy to
   *  "here's what this is". */
  emptyHint?: string
  tone?: 'neutral' | 'attention'
  loading?: boolean
}) {
  return (
    <Link
      to={to}
      className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <Card className="h-full p-4 transition-colors hover:bg-accent">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
        {loading ? (
          // Skeleton in the tile's own shape — a full-page spinner would hide
          // the sections that already have their data.
          <Skeleton className="mt-2 h-8 w-14" />
        ) : (
          <p
            className={cn(
              'mt-1 font-mono text-h2 font-semibold tabular-nums',
              tone === 'attention' && value > 0 && 'text-warning',
            )}
          >
            {value}
          </p>
        )}
        <p className="mt-1 text-xs text-muted-foreground">
          {value === 0 && !loading && emptyHint ? emptyHint : hint}
        </p>
      </Card>
    </Link>
  )
}

/**
 * A number with no pre-filtered destination to send it to.
 *
 * Used only by the maintainer pipeline funnel: `GET /studies?study_state=` is
 * the source, but no *route* takes a study state without a collection id, so a
 * link would land on an unfiltered list. Rendering it as a figure and saying so
 * is more honest than a link that drops the filter.
 */
export function Figure({
  label,
  value,
  hint,
  loading = false,
}: {
  label: string
  value: number
  hint: string
  loading?: boolean
}) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      {loading ? (
        <Skeleton className="mt-2 h-8 w-14" />
      ) : (
        <p className="mt-1 font-mono text-h2 font-semibold tabular-nums">{value}</p>
      )}
      <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
    </div>
  )
}

/** One row in a short list. A real link, so middle-click and focus both work. */
export function RowLink({
  to,
  primary,
  secondary,
  right,
}: {
  to: string
  primary: ReactNode
  secondary: ReactNode
  right?: ReactNode
}) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between gap-3 rounded-md border bg-card p-3 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">{primary}</span>
        <span className="block truncate text-xs text-muted-foreground">{secondary}</span>
      </span>
      {right && <span className="shrink-0 text-xs text-muted-foreground">{right}</span>}
    </Link>
  )
}

/**
 * The most-seen state on this page. Never "No data": say what is true and what
 * to do next, because a new technician has zero of everything and a maintainer
 * with nothing flagged should feel finished, not broken.
 */
export function EmptyNote({ children }: { children: ReactNode }) {
  return (
    <Card className="p-4">
      <p className="text-sm text-muted-foreground">{children}</p>
    </Card>
  )
}

export function RowSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-[58px] w-full" />
      ))}
    </div>
  )
}

/** `YYYY-MM-DD`, `days` ago — the shape `/activity?from=` expects. */
export function daysAgoISODate(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10)
}
